"use client";

import {
  Field,
  Text,
  withDatasourceCheck,
  useSitecoreContext,
} from "@sitecore-jss/sitecore-jss-nextjs";
import clsx from "clsx";
import { useState, useEffect, SyntheticEvent } from "react";
import * as Flex from "@repo/ui/flex";
import { Select as SelectUI, Option as OptionUI } from "@repo/ui/select";
import { TYPOGRAPHY_LABEL3 } from "@/typography-constants";
import { ComponentProps } from "../../../../types/component-props";
import { isMobile } from "../../../utils";
import { ChevronD, Download } from "./icons";
import styles from "./attachments.module.css";

type JsonValueType = {
  value: {
    src: string;
    name: string;
    displayName: string;
    title: string;
    keywords: string;
    description: string;
    extension: string;
    mimeType: string;
    size: string;
  };
};

type FileType = {
  name: string;
  file: {
    jsonValue: JsonValueType;
  };
};

type AttachmentsProps = ComponentProps & {
  fields: {
    data: {
      datasource: {
        buttonText: Field<string>;
        attachments: { items: FileType[] };
      };
    };
  };
};

const Attachments = (props: AttachmentsProps): JSX.Element => {
  const { sitecoreContext } = useSitecoreContext();
  const datasource = props?.fields?.data?.datasource;
  const isEditing = sitecoreContext?.pageEditing;

  if (datasource.attachments?.items.length === 0 && !isEditing) {
    return <></>;
  }

  const { attachments, buttonText } = datasource;
  const [selectedValue, setSelectedValue] = useState<FileType | undefined>(
    undefined,
  );
  const [buttonLabel, setButtonLabel] = useState<Field<string>>(buttonText);

  const Select = isEditing ? "select" : SelectUI;
  const Option = isEditing ? "option" : OptionUI;

  useEffect(() => {
    if (attachments?.items.length > 0) {
      const firstFile = attachments.items[0];
      setSelectedValue(firstFile);
    }
    if (buttonText.value) {
      setButtonLabel(buttonText);
    }
  }, [attachments.items.length, buttonText.value]);

  const handleSelect = (
    valueOrEvent:
      | string
      | SyntheticEvent<HTMLSelectElement | HTMLButtonElement>,
  ) => {
    let value: string;
    if (typeof valueOrEvent === "string") {
      value = valueOrEvent;
    } else {
      const target = valueOrEvent.target as
        | HTMLSelectElement
        | HTMLButtonElement;
      value = target.value || "";
    }
    if (!value) return;

    const file = attachments.items.find(
      (item) => item.file.jsonValue.value.src === value,
    );
    if (file) setSelectedValue(file);
  };

  const mobileView = isMobile();

  return (
    <Flex.Container
      column={mobileView as boolean}
      justify="center"
      align="center"
      gap="lg"
      className={styles.faqAttachmentContainer}
    >
      <Select
        className={clsx(styles.select, TYPOGRAPHY_LABEL3)}
        onSelect={handleSelect}
        value={selectedValue?.file.jsonValue.value.src}
        placeholder={selectedValue?.file.jsonValue.value.displayName as string}
        aria-labelledby="attachment-select-label"
        icon={<ChevronD width={16} height={16} />}
      >
        {(attachments.items as FileType[]).map((item, index) => {
          return (
            <Option
              data-testid={`option-${item.file.jsonValue.value.name}`}
              key={index}
              value={item.file.jsonValue.value.src as string}
            >
              <span className={clsx(styles.optionText, TYPOGRAPHY_LABEL3)}>
                {item.file.jsonValue.value.displayName as string}
              </span>
            </Option>
          );
        })}
      </Select>
      <a
        className={styles.downloadButton}
        href={selectedValue?.file.jsonValue.value.src}
        download
        aria-label={`Download attachment ${selectedValue?.file.jsonValue.value.displayName || "file"}`}
      >
        <span className={styles.downloadIcon} aria-hidden="true">
          <Download width={20} height={20} />
        </span>
        <Text tag="span" field={buttonLabel} className={TYPOGRAPHY_LABEL3} />
      </a>
    </Flex.Container>
  );
};

export default withDatasourceCheck()<AttachmentsProps>(Attachments);
